import { useState, useEffect } from 'react'
import { Sun, Moon, Bell, Volume2 } from 'lucide-react'
import { useAuth } from '../context/AuthContext.js'
import { useTheme } from '../context/ThemeContext.js'
import './Settings.css'

export default function Settings() {
  const { apiFetch, admin, features } = useAuth()
  const { theme, setTheme } = useTheme()

  // ── Dashboard notifications (localStorage, browser-level) ─────────────────
  const [dashPopup, setDashPopupState] = useState(
    () => localStorage.getItem('dashboard-notify-enabled') !== 'false'
  )
  const [dashSound, setDashSoundState] = useState(
    () => localStorage.getItem('dashboard-notify-sound') !== 'false'
  )

  const setDashPopup = (val) => {
    localStorage.setItem('dashboard-notify-enabled', String(val))
    setDashPopupState(val)
  }
  const setDashSound = (val) => {
    localStorage.setItem('dashboard-notify-sound', String(val))
    setDashSoundState(val)
  }

  // ── Notification preferences (per user) ───────────────────────────────────
  const [checkinNotify,     setCheckinNotify]     = useState(false)
  const [smsNotify,         setSmsNotify]         = useState(false)
  const [notificationPhone, setNotificationPhone] = useState('')
  const [notifLoading,      setNotifLoading]      = useState(true)
  const [notifSaving,       setNotifSaving]       = useState(false)
  const [notifMsg,          setNotifMsg]          = useState(null)

  useEffect(() => {
    apiFetch('/api/admin/notification-prefs')
      .then(r => r.json())
      .then(data => {
        setCheckinNotify(data.checkinNotify ?? false)
        setSmsNotify(data.smsNotify ?? false)
        setNotificationPhone(data.notificationPhone ?? '')
      })
      .catch(() => {})
      .finally(() => setNotifLoading(false))
  }, [])

  const saveNotifPrefs = async (patch) => {
    setNotifSaving(true)
    try {
      const res = await apiFetch('/api/admin/notification-prefs', {
        method: 'PUT',
        body:   JSON.stringify(patch),
      })
      if (!res.ok) throw new Error()
      setNotifMsg({ type: 'success', text: 'Gespeichert.' })
    } catch {
      setNotifMsg({ type: 'error', text: 'Fehler beim Speichern.' })
    } finally {
      setNotifSaving(false)
      setTimeout(() => setNotifMsg(null), 3000)
    }
  }

  const handleCheckinNotify = (val) => {
    setCheckinNotify(val)
    saveNotifPrefs({ checkinNotify: val })
  }

  const handleSmsToggle = (val) => {
    setSmsNotify(val)
    saveNotifPrefs({ smsNotify: val })
  }

  const handlePhoneSave = () => {
    saveNotifPrefs({ notificationPhone })
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div>
      <div className="page-header">
        <h1>Einstellungen</h1>
        <p>Erscheinungsbild, Sicherheit und Benachrichtigungen.</p>
      </div>

      {/* ── Appearance ────────────────────────────────────────────────────── */}
      <div className="card settings-section">
        <div className="section-title">Erscheinungsbild</div>
        <div className="settings-theme-row">
          <button
            className={`settings-theme-btn${theme === 'dark' ? ' active' : ''}`}
            onClick={() => setTheme('dark')}
          >
            <Moon size={18} strokeWidth={1.8} />
            Dunkles Design
          </button>
          <button
            className={`settings-theme-btn${theme === 'light' ? ' active' : ''}`}
            onClick={() => setTheme('light')}
          >
            <Sun size={18} strokeWidth={1.8} />
            Helles Design
          </button>
        </div>
      </div>

      {/* ── Dashboard notifications ───────────────────────────────────────── */}
      <div className="card settings-section">
        <div className="section-title">Dashboard-Benachrichtigungen</div>
        <p className="settings-section-desc">
          Echtzeit-Benachrichtigungen in diesem Browser, wenn ein Besucher eincheckt.
        </p>
        <div className="settings-channels">
          <div className={`settings-channel${dashPopup ? ' settings-channel--active' : ''}`}>
            <div className="settings-channel-header">
              <div className="settings-channel-label">
                <Bell size={14} strokeWidth={1.8} />
                Pop-up anzeigen
              </div>
              <label className="toggle">
                <input type="checkbox" checked={dashPopup} onChange={e => setDashPopup(e.target.checked)} />
                <span className="toggle-slider" />
              </label>
            </div>
          </div>
          <div className={`settings-channel${dashSound ? ' settings-channel--active' : ''}`}>
            <div className="settings-channel-header">
              <div className="settings-channel-label">
                <Volume2 size={14} strokeWidth={1.8} />
                Ton abspielen
              </div>
              <label className="toggle">
                <input type="checkbox" checked={dashSound} onChange={e => setDashSound(e.target.checked)} />
                <span className="toggle-slider" />
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* ── Notification preferences ──────────────────────────────────────── */}
      <div className="card settings-section">
        <div className="section-title">Benachrichtigungen</div>
        <p className="settings-section-desc">
          Benachrichtigungen für Ihren persönlichen Account.
        </p>

        {notifLoading ? (
          <div className="loading-state"><span className="spinner" /> Laden…</div>
        ) : (
          <>
            {features?.email_enabled !== false && (
              <div className={`settings-channel${checkinNotify ? ' settings-channel--active' : ''}`}>
                <div className="settings-channel-header">
                  <div className="settings-channel-label">
                    <Bell size={14} strokeWidth={1.8} />
                    E-Mail bei Check-in
                  </div>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={checkinNotify}
                      disabled={notifSaving}
                      onChange={e => handleCheckinNotify(e.target.checked)}
                    />
                    <span className="toggle-slider" />
                  </label>
                </div>
                {checkinNotify && (
                  <div className="settings-channel-body">
                    <p className="settings-notif-body-text">
                      Benachrichtigungen werden an <strong>{admin?.email}</strong> gesendet.
                    </p>
                  </div>
                )}
              </div>
            )}

            {features?.sms_enabled !== false && (
              <div className={`settings-channel${smsNotify ? ' settings-channel--active' : ''}`} style={{ marginTop: 12 }}>
                <div className="settings-channel-header">
                  <div className="settings-channel-label">
                    <Bell size={14} strokeWidth={1.8} />
                    SMS bei Check-in
                  </div>
                  <label className="toggle">
                    <input
                      type="checkbox"
                      checked={smsNotify}
                      disabled={notifSaving}
                      onChange={e => handleSmsToggle(e.target.checked)}
                    />
                    <span className="toggle-slider" />
                  </label>
                </div>
                {smsNotify && (
                  <div className="settings-channel-body">
                    <p className="settings-notif-body-text">SMS wird gesendet an:</p>
                    <div className="settings-phone-row">
                      <input
                        className="form-control"
                        placeholder="+49 170 1234567"
                        value={notificationPhone}
                        onChange={e => setNotificationPhone(e.target.value)}
                      />
                      <button className="btn btn-primary btn-sm" onClick={handlePhoneSave} disabled={notifSaving}>
                        Speichern
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {notifMsg && (
          <div className={`alert ${notifMsg.type === 'success' ? 'alert-success' : 'alert-error'} settings-alert settings-notif-msg`}>
            {notifMsg.text}
          </div>
        )}
      </div>
    </div>
  )
}
